import { useEffect, useRef, useState } from "react";
import { Film, Link2, Loader2, Upload, X, CheckCircle2 } from "lucide-react";
import { adminApi } from "../api";
import { resolveImage } from "../../lib/data";
import { toast } from "sonner";

const MAX_MB = 100;
const ALLOWED_MIME = ["video/mp4", "video/webm"];

function formatMB(bytes) {
  if (!bytes) return "";
  return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
}

/**
 * VideoUploader — Upload self-hosted video (MP4/WebM, max 100 MB) OR use external URL.
 * Emits the resolved URL via onChange (relative "/api/files/…" when uploaded).
 */
export default function VideoUploader({ value, onChange, testId = "video-uploader" }) {
  const isUploadedUrl = typeof value === "string" && value.startsWith("/api/files/");
  const [mode, setMode] = useState(isUploadedUrl ? "upload" : "url");
  const [url, setUrl] = useState(value || "");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [meta, setMeta] = useState(null); // { size, filename }
  const fileRef = useRef(null);

  useEffect(() => {
    setUrl(value || "");
  }, [value]);

  const validateFile = (file) => {
    if (!ALLOWED_MIME.includes(file.type)) {
      toast.error("Formato non supportato", {
        description: "Solo MP4 o WebM sono accettati",
      });
      return false;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error("Video troppo grande", {
        description: `Massimo ${MAX_MB} MB (attuale: ${formatMB(file.size)})`,
      });
      return false;
    }
    return true;
  };

  const uploadFile = async (file) => {
    if (!validateFile(file)) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    setProgress(0);
    try {
      const res = await adminApi.post("/admin/upload-video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) {
            setProgress(Math.round((evt.loaded * 100) / evt.total));
          }
        },
        timeout: 15 * 60 * 1000, // 15 min per file grandi
      });
      onChange(res.data.url);
      setUrl(res.data.url);
      setMeta({ size: res.data.size, filename: res.data.filename });
      toast.success("Video caricato", {
        description: `${res.data.filename} · ${formatMB(res.data.size)}`,
      });
    } catch (err) {
      toast.error("Upload fallito", {
        description: err.response?.data?.detail || err.message || "Riprova",
      });
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const remove = () => {
    setUrl("");
    setMeta(null);
    onChange("");
  };

  return (
    <div data-testid={testId} className="space-y-3">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setMode("url")}
          data-testid={`${testId}-mode-url`}
          className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
            mode === "url"
              ? "bg-white text-black border-white"
              : "border-white/15 text-white/60 hover:text-white"
          }`}
        >
          <Link2 size={12} className="inline mr-1" /> URL esterno (CDN)
        </button>
        <button
          type="button"
          onClick={() => setMode("upload")}
          data-testid={`${testId}-mode-upload`}
          className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
            mode === "upload"
              ? "bg-white text-black border-white"
              : "border-white/15 text-white/60 hover:text-white"
          }`}
        >
          <Upload size={12} className="inline mr-1" /> Carica file (self-hosted)
        </button>
      </div>

      {mode === "url" ? (
        <input
          type="url"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            onChange(e.target.value);
          }}
          placeholder="https://cdn.esempio.com/hero.mp4"
          data-testid={`${testId}-url-input`}
          className="w-full bg-transparent border-b border-white/15 focus:border-[#C48F56] outline-none py-2 text-sm"
        />
      ) : (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            if (!uploading) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`relative rounded-lg border-2 border-dashed transition-colors ${
            dragOver
              ? "border-[#C48F56] bg-[#C48F56]/5"
              : "border-white/15 hover:border-white/25 hover:bg-white/[0.02]"
          } ${uploading ? "opacity-70" : "cursor-pointer"}`}
          onClick={() => !uploading && fileRef.current?.click()}
          data-testid={`${testId}-dropzone`}
        >
          <input
            ref={fileRef}
            type="file"
            accept="video/mp4,video/webm"
            onChange={onFileInput}
            className="hidden"
            data-testid={`${testId}-file-input`}
          />

          <div className="p-6 text-center">
            {uploading ? (
              <>
                <Loader2 size={24} className="mx-auto mb-3 animate-spin text-[#C48F56]" />
                <div className="text-sm text-white/80">Caricamento in corso…</div>
                <div className="mt-3 h-1.5 w-full max-w-xs mx-auto rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full bg-[#C48F56] transition-all duration-150"
                    style={{ width: `${progress}%` }}
                    data-testid={`${testId}-progress-bar`}
                  />
                </div>
                <div className="mt-2 text-[11px] font-mono text-white/50">
                  {progress}%
                </div>
              </>
            ) : (
              <>
                <div className="mx-auto mb-3 h-11 w-11 rounded-full bg-white/5 grid place-items-center text-white/60">
                  <Film size={20} />
                </div>
                <div className="text-sm text-white/80">
                  Trascina qui il video o <span className="text-[#C48F56] underline underline-offset-2">scegli un file</span>
                </div>
                <div className="mt-1 text-[11px] text-white/40">
                  MP4 o WebM · massimo {MAX_MB} MB · consigliato ≤ 6 MB per 40s
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {url && !uploading && (
        <div className="relative rounded-lg border border-white/10 bg-black/30 p-3">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-[#C48F56]/15 grid place-items-center text-[#C48F56] shrink-0">
              <CheckCircle2 size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">
                {meta?.filename || (isUploadedUrl ? "Video self-hosted" : "URL esterno")}
              </div>
              <div className="mt-0.5 text-[11px] font-mono text-white/40 truncate">
                {url} {meta?.size ? `· ${formatMB(meta.size)}` : ""}
              </div>
              <video
                src={resolveImage(url)}
                controls
                muted
                preload="metadata"
                className="mt-3 w-full max-w-md rounded-md border border-white/10 bg-black"
                data-testid={`${testId}-preview`}
              />
            </div>
            <button
              type="button"
              onClick={remove}
              className="h-8 w-8 grid place-items-center rounded-full hover:bg-red-500/10 hover:text-red-400 text-white/50"
              aria-label="Rimuovi"
              data-testid={`${testId}-remove`}
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
